import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CenteredLoading from "./CenteredLoading";

//const videoId = "MxYMW1RqdgQ";

type OnPlayerPlay = () => void;

interface Props {
  videoId: string;
  onPlayerReady: (player: any) => void;
  onPlayerStateChange: (event: any) => void;
  //called when it starts the first time and whenever it goes from paused to play
  onPlayerPlay: () => void;
}

const EmbeddedYoutubeVideo: React.FC<Props> = ({
  onPlayerReady,
  onPlayerStateChange,
  onPlayerPlay,
  videoId,
}) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);
  const lastStateRef = useRef<number | undefined>(undefined);
  const onPlayerPlayRef = useRef<OnPlayerPlay | undefined>(onPlayerPlay);
  useEffect(() => {
    onPlayerPlayRef.current = onPlayerPlay;
  }, [onPlayerPlay]);

  useEffect(() => {
    setIsIframeLoaded(false);
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);

    (window as any).onYouTubeIframeAPIReady = () => {
      setIsIframeLoaded(true);
      new (window as any).YT.Player("ytplayer", {
        videoId: videoId,
        events: {
          onReady: (e: any) => {
            onPlayerReady(e);
          },
          onStateChange: (e: any) => {
            const isVideoPlayingNow =
              e.data === (window as any).YT.PlayerState.PLAYING ||
              e.data === (window as any).YT.PlayerState.BUFFERING;
            if (
              isVideoPlayingNow &&
              lastStateRef.current !== (window as any).YT.PlayerState.PLAYING &&
              lastStateRef.current !== (window as any).YT.PlayerState.BUFFERING
            ) {
              if (onPlayerPlayRef.current) {
                onPlayerPlayRef.current();
              }
            }
            lastStateRef.current = e.data;
            onPlayerStateChange(e);
          },
        },
      });
    };
  }, [videoId]);
  //console.log("isIframeLoaded", isIframeLoaded);

  return (
    <div
      className={clsx(["w-full h-[400px]"], {
        "border border-red-500": !isIframeLoaded,
        //"border border-blue-500": isIframeLoaded,
      })}
    >
      {!isIframeLoaded && <CenteredLoading />}
      <div id="ytplayer" className="w-full h-full"></div>
    </div>
  );
};

export default EmbeddedYoutubeVideo;

export function useEmbeddedYoutubeVideo(): {
  onPlayerReady: (player: any) => void;
  onPlayerStateChange: (event: any) => void;

  currentTime: number | undefined;
  seekTo: (timeStamp: number) => Promise<void>;
  //playPause maybe should return a promise?
  playPause: () => void;
  state: number | undefined;
  //convenience field for checking state ^
  isPlayingOrBuffering: boolean;
} {
  const checkTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const playerRef = useRef<any>(null);
  const stateRef = useRef<number>(-1);
  const seekToIntervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [currentTime, setCurrentTime] = useState(undefined);
  const [state, setState] = useState<number | undefined>(undefined);

  useEffect(() => {
    //TODO: this is wrong...
    checkTimeIntervalRef.current = setInterval(() => {
      const time = playerRef.current?.getCurrentTime();
      setCurrentTime(time);
    }, 250);
    return () => {
      if (checkTimeIntervalRef.current) {
        clearInterval(checkTimeIntervalRef.current);
      }
    };
  }, []);

  const onPlayerReady = useCallback((event: any) => {
    playerRef.current = event.target;
  }, []);

  const onPlayerStateChange = useCallback((event: any) => {
    setState(event.data);
    stateRef.current = event.data;
  }, []);

  const seekTo = useCallback((timeStamp: number): Promise<void> => {
    playerRef.current?.seekTo(timeStamp, true);
    if (seekToIntervalIdRef.current !== null) {
      clearInterval(seekToIntervalIdRef.current);
      seekToIntervalIdRef.current = null;
    }
    return new Promise<void>((res) => {
      seekToIntervalIdRef.current = setInterval(() => {
        console.log(
          `checking if video is playing and at target ts of ${timeStamp} - interval ${seekToIntervalIdRef.current}`
        );
        const timeNow = Math.round(playerRef.current?.getCurrentTime());
        const isPlaying =
          stateRef.current === (window as any).YT.PlayerState.PLAYING;
        if (isPlaying && timeNow >= timeStamp) {
          console.log(
            `video finally playing and at (or further than) target ts of ${timeStamp}`
          );
          if (seekToIntervalIdRef.current !== null) {
            clearInterval(seekToIntervalIdRef.current);
            seekToIntervalIdRef.current = null;
          }
          seekToIntervalIdRef.current = null;
          res();
        } else {
          console.log(
            `video not ready yet - state is ${stateRef.current} and ts is ${timeNow}`
          );
        }
      }, 250);
    });
  }, []);

  const playPause = useCallback(() => {
    if (
      stateRef.current === (window as any).YT.PlayerState.PLAYING ||
      stateRef.current === (window as any).YT.PlayerState.BUFFERING
    ) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, []);

  return {
    onPlayerReady,
    onPlayerStateChange,
    currentTime:
      currentTime === undefined ? currentTime : Math.round(currentTime),
    seekTo,
    state,
    isPlayingOrBuffering:
      state !== undefined &&
      (state === (window as any).YT.PlayerState.PLAYING ||
        state === (window as any).YT.PlayerState.BUFFERING),
    playPause,
  };
}

//onStateChange
//This event fires whenever the player's state changes. The data property of the event object that the API passes to your event listener function will specify an integer that corresponds to the new player state. Possible values are:
//-1 (unstarted)
//0 (ended)
//1 (playing)
//2 (paused)
//3 (buffering)
//5 (video cued).
//When the player first loads a video, it will broadcast an unstarted (-1) event. When a video is cued and ready to play, the player will broadcast a video cued (5) event. In your code, you can specify the integer values or you can use one of the following namespaced variables:
//YT.PlayerState.ENDED
//YT.PlayerState.PLAYING
//YT.PlayerState.PAUSED
//YT.PlayerState.BUFFERING
//YT.PlayerState.CUED
//https://developers.google.com/youtube/iframe_api_reference#seekTo
//
//"UNSTARTED": -1,
//"ENDED": 0,
//"PLAYING": 1,
//"PAUSED": 2,
//"BUFFERING": 3,
//"CUED": 5
